/**
 * Routage et gardes d'accès.
 *
 * Les gardes côté client servent uniquement le confort de navigation : la
 * sécurité réelle est appliquée par le serveur, qui refuse toute requête non
 * autorisée. Un contournement de ces redirections ne donne donc accès à rien.
 */

import { Navigate, Route, Routes, useLocation } from "react-router-dom";

import { AppLayout } from "./components/Layout";
import { LoadingBlock } from "./components/ui";
import { useSession } from "./hooks/SessionContext";
import { BusinessProfilePage } from "./pages/BusinessProfilePage";
import { DashboardPage } from "./pages/DashboardPage";
import { DataPage } from "./pages/DataPage";
import { DocumentsPage } from "./pages/DocumentsPage";
import { HistoryPage } from "./pages/HistoryPage";
import { LoginPage } from "./pages/LoginPage";
import { NotFoundPage } from "./pages/NotFoundPage";
import { NotificationsPage } from "./pages/NotificationsPage";
import { OnboardingPage } from "./pages/OnboardingPage";
import {
  PasswordResetConfirmPage,
  PasswordResetRequestPage,
} from "./pages/PasswordResetPages";
import { RegisterPage } from "./pages/RegisterPage";
import { RulesPage } from "./pages/RulesPage";
import { SecurityPage } from "./pages/SecurityPage";
import { SettingsPage } from "./pages/SettingsPage";
import { TaskDetailPage } from "./pages/TaskDetailPage";
import { TaskFormPage } from "./pages/TaskFormPage";
import { TasksPage } from "./pages/TasksPage";
import { TrashPage } from "./pages/TrashPage";
import { TwoFactorChallengePage } from "./pages/TwoFactorChallengePage";
import { VerifyEmailPage } from "./pages/VerifyEmailPage";

function FullPageLoader() {
  return (
    <div className="auth-layout">
      <LoadingBlock label="Chargement de votre espace…" />
    </div>
  );
}

/** Route accessible uniquement à un utilisateur pleinement authentifié. */
function Protected({ children }: { children: React.ReactNode }) {
  const { loading, state, isAuthenticated, needsTwoFactor, needsOnboarding } =
    useSession();
  const location = useLocation();

  if (loading) return <FullPageLoader />;
  if (!state?.authenticated) {
    return <Navigate to="/connexion" replace state={{ from: location.pathname }} />;
  }
  if (needsTwoFactor) return <Navigate to="/verification-2fa" replace />;
  if (needsOnboarding && location.pathname !== "/bienvenue") {
    return <Navigate to="/bienvenue" replace />;
  }
  if (!isAuthenticated) return <FullPageLoader />;
  return <AppLayout>{children}</AppLayout>;
}

/** Route réservée aux visiteurs non connectés. */
function PublicOnly({ children }: { children: React.ReactNode }) {
  const { loading, state, needsTwoFactor } = useSession();
  if (loading) return <FullPageLoader />;
  if (state?.authenticated && needsTwoFactor) {
    return <Navigate to="/verification-2fa" replace />;
  }
  if (state?.authenticated) return <Navigate to="/" replace />;
  return <>{children}</>;
}

export function App() {
  const { loading, state, needsTwoFactor, needsOnboarding } = useSession();

  return (
    <Routes>
      {/* --- Parcours public ------------------------------------------- */}
      <Route
        path="/connexion"
        element={
          <PublicOnly>
            <LoginPage />
          </PublicOnly>
        }
      />
      <Route
        path="/creer-un-compte"
        element={
          <PublicOnly>
            <RegisterPage />
          </PublicOnly>
        }
      />
      {/* La vérification d'e-mail reste accessible dans les deux états :
          l'utilisateur peut ouvrir le lien avant ou après s'être connecté. */}
      <Route path="/verifier-email" element={<VerifyEmailPage />} />
      <Route
        path="/mot-de-passe-oublie"
        element={
          <PublicOnly>
            <PasswordResetRequestPage />
          </PublicOnly>
        }
      />
      <Route
        path="/nouveau-mot-de-passe"
        element={<PasswordResetConfirmPage />}
      />

      {/* --- Étapes intermédiaires ------------------------------------- */}
      <Route
        path="/verification-2fa"
        element={
          loading ? (
            <FullPageLoader />
          ) : !state?.authenticated ? (
            <Navigate to="/connexion" replace />
          ) : !needsTwoFactor ? (
            <Navigate to="/" replace />
          ) : (
            <TwoFactorChallengePage />
          )
        }
      />
      <Route
        path="/bienvenue"
        element={
          loading ? (
            <FullPageLoader />
          ) : !state?.authenticated ? (
            <Navigate to="/connexion" replace />
          ) : needsTwoFactor ? (
            <Navigate to="/verification-2fa" replace />
          ) : !needsOnboarding ? (
            <Navigate to="/" replace />
          ) : (
            <OnboardingPage />
          )
        }
      />

      {/* --- Application ----------------------------------------------- */}
      <Route
        path="/"
        element={
          <Protected>
            <DashboardPage />
          </Protected>
        }
      />
      <Route
        path="/demarches"
        element={
          <Protected>
            <TasksPage />
          </Protected>
        }
      />
      <Route
        path="/demarches/nouvelle"
        element={
          <Protected>
            <TaskFormPage mode="create" />
          </Protected>
        }
      />
      <Route
        path="/demarches/:taskId"
        element={
          <Protected>
            <TaskDetailPage />
          </Protected>
        }
      />
      <Route
        path="/demarches/:taskId/modifier"
        element={
          <Protected>
            <TaskFormPage mode="edit" />
          </Protected>
        }
      />
      <Route
        path="/documents"
        element={
          <Protected>
            <DocumentsPage />
          </Protected>
        }
      />
      <Route
        path="/historique"
        element={
          <Protected>
            <HistoryPage />
          </Protected>
        }
      />
      <Route
        path="/notifications"
        element={
          <Protected>
            <NotificationsPage />
          </Protected>
        }
      />
      <Route
        path="/bibliotheque"
        element={
          <Protected>
            <RulesPage />
          </Protected>
        }
      />
      <Route
        path="/profil-entreprise"
        element={
          <Protected>
            <BusinessProfilePage />
          </Protected>
        }
      />
      <Route
        path="/parametres"
        element={
          <Protected>
            <SettingsPage />
          </Protected>
        }
      />
      <Route
        path="/securite"
        element={
          <Protected>
            <SecurityPage />
          </Protected>
        }
      />
      <Route
        path="/donnees"
        element={
          <Protected>
            <DataPage />
          </Protected>
        }
      />
      <Route
        path="/corbeille"
        element={
          <Protected>
            <TrashPage />
          </Protected>
        }
      />
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}
